import React from 'react'
import styled from 'styled-components'

import { JutakuSaigaiMimaiKinData } from '../../../../types/formData'

import { Heading } from '../../../ui/Heading'
import { Input } from '../../../ui/Input'
import { DatePicker } from '../../../ui/DatePicker'
import { Controller, useFormContext } from 'react-hook-form'
import {
  BankAccountSection,
  FileSection,
  MemberInfoSection,
} from './inputParts'
import { MessageColumnSection } from '../../../layout/inputParts/MessageColumnSection'
import { mediaQuery } from '../../../../themes/size'
import { isYYYYMMDD } from '../../../../util/validation'

type Props = {
  disabled: boolean
  canAdminUpload?: boolean
  handleSaveImage?: (files: File) => Promise<void>
  isUploading?: boolean
  handleDeleteImage?: (fileId: string) => Promise<void>
  shouldShowOtherFormLinks?: true
}

const Component: React.FC<Props> = (props) => {
  const methods = useFormContext<JutakuSaigaiMimaiKinData['formData']>()
  const { control, errors } = methods

  return (
    <>
      <HeadingArea>
        <Heading type="screenTitle" tag="h1">
          ▼給付金申請 - 住宅災害見舞金
        </Heading>
      </HeadingArea>

      {Object.keys(methods.errors).length > 0 && (
        <Section>
          <ErrorMessage>
            入力内容に不備があります。各入力項目のメッセージを参照してください。
          </ErrorMessage>
        </Section>
      )}

      <MemberInfoSection
        shouldShowOtherFormLinks={props.shouldShowOtherFormLinks}
      />

      <Section>
        <Heading type="sectionTitle" tag="h2">
          ■申請情報
        </Heading>
        <InputField>
          <Heading type="blockTitle" tag="span">
            災害の種類
          </Heading>
          <Controller
            control={control}
            name="disasterType"
            rules={{ required: '災害の種類を入力してください' }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                value={value}
                onChange={onChange}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        {errors.disasterType && (
          <ErrorMessage>{errors.disasterType.message}</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            発生日
          </Heading>
          <Controller
            control={control}
            name="disasterDate"
            rules={{
              required: '発生日を入力してください',
              validate: { isYYYYMMDD },
            }}
            render={({ onChange, value, onBlur }) => (
              <DatePicker
                onChange={onChange}
                value={value}
                disabled={props.disabled}
                onBlur={onBlur}
              />
            )}
          />
        </InputField>
        {errors.disasterDate && (
          <ErrorMessage>{errors.disasterDate.message}</ErrorMessage>
        )}
        {errors.disasterDate?.type === 'isYYYYMMDD' && (
          <ErrorMessage>YYYY/MM/DDの形式で入力して下さい</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            損害の程度
          </Heading>
          <Controller
            control={control}
            name="disasterLevel"
            rules={{ required: '損害の程度を入力してください' }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                onChange={onChange}
                value={value}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        {errors.disasterLevel && (
          <ErrorMessage>{errors.disasterLevel.message}</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            請求金額
          </Heading>
          <LabelWrapper>
            <Controller
              control={control}
              name="amount"
              defaultValue="10,000"
              render={({ onChange, value, onBlur }) => (
                <Input
                  type="text"
                  onChange={onChange}
                  value={value}
                  disabled
                  onBlurCapture={onBlur}
                />
              )}
            />
            <InputLabel>円</InputLabel>
          </LabelWrapper>
        </InputField>
      </Section>

      <FileSection
        headingLabel="災害証明書など、公的期間の証明書"
        disabled={props.disabled}
        canAdminUpload={props.canAdminUpload}
        handleSaveImage={props.handleSaveImage}
        isUploading={props.isUploading}
        handleDeleteImage={props.handleDeleteImage}
      />

      <BankAccountSection disabled={props.disabled} isMember={true} />

      <MessageColumnSection disabled={props.disabled} />
    </>
  )
}

const HeadingArea = styled.div`
  margin-top: 32px;
`

const Section = styled.section`
  margin-top: 72px;
`
const InputField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 510px;
  margin-top: 16px;

  @media screen and (max-width: ${mediaQuery.SP}px) {
    display: block;
    width: 100%;
    & > span {
      &:first-child {
        margin-bottom: 8px;
      }
    }
  }
`
const ErrorMessage = styled.div`
  color: red;
`
const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 328px;
`
const InputLabel = styled.label`
  margin-left: 8px;
  font-size: 16px;
`
export default Component
